import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const GeographicImpactMap = () => {
  const [selectedDistrict, setSelectedDistrict] = useState(null);

  const districts = [
    {
      id: 1,
      name: "Colombo Central",
      coordinates: { lat: 6.9271, lng: 79.8612 },
      projects: 12,
      beneficiaries: 2450,
      color: "bg-emerald-500",
      description: "Focus on education and healthcare initiatives in urban communities."
    },
    {
      id: 2,
      name: "Colombo North",
      coordinates: { lat: 6.9497, lng: 79.8681 },
      projects: 8,
      beneficiaries: 1680,
      color: "bg-blue-500",
      description: "Environmental conservation and youth development programs."
    },
    {
      id: 3,
      name: "Colombo South",
      coordinates: { lat: 6.8649, lng: 79.8997 },
      projects: 15,
      beneficiaries: 3200,
      color: "bg-purple-500",
      description: "Community health and economic empowerment initiatives."
    },
    {
      id: 4,
      name: "Dehiwala-Mount Lavinia",
      coordinates: { lat: 6.8344, lng: 79.8707 },
      projects: 6,
      beneficiaries: 980,
      color: "bg-amber-500",
      description: "Coastal conservation and fishermen community support."
    },
    {
      id: 5,
      name: "Moratuwa",
      coordinates: { lat: 6.7730, lng: 79.8816 },
      projects: 4,
      beneficiaries: 640,
      color: "bg-red-500",
      description: "Technical education and skills development programs."
    }
  ];

  const totalProjects = districts.reduce((sum, district) => sum + district.projects, 0);
  const totalBeneficiaries = districts.reduce((sum, district) => sum + district.beneficiaries, 0);

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Geographic Impact Distribution
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Our service projects span across Colombo districts, creating meaningful impact in diverse communities throughout the metropolitan area.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Map Section */}
          <div className="bg-card rounded-xl p-6 card-shadow">
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-foreground mb-2">Service Distribution Map</h3>
              <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                <span>Total Projects: <strong className="text-foreground">{totalProjects}</strong></span>
                <span>Total Beneficiaries: <strong className="text-foreground">{totalBeneficiaries.toLocaleString()}</strong></span>
              </div>
            </div>

            {/* Interactive Map */}
            <div className="relative h-96 bg-slate-100 rounded-lg overflow-hidden">
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title="Leo Club Colombo Service Areas"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=6.9271,79.8612&z=12&output=embed"
                className="rounded-lg"
              />
              
              {/* Overlay with district markers */}
              <div className="absolute inset-0 pointer-events-none">
                {districts.map((district, index) => (
                  <div
                    key={district.id}
                    className={`absolute w-4 h-4 ${district.color} rounded-full border-2 border-white shadow-lg pointer-events-auto cursor-pointer hover:scale-125 transition-transform`}
                    style={{
                      left: `${20 + (index * 15)}%`,
                      top: `${30 + (index * 10)}%`
                    }}
                    onClick={() => setSelectedDistrict(district)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* District Details */}
          <div className="space-y-6">
            <div className="bg-card rounded-xl p-6 card-shadow">
              <h3 className="text-xl font-semibold text-foreground mb-4">District Overview</h3>
              
              {selectedDistrict ? (
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 ${selectedDistrict.color} rounded-full`} />
                    <h4 className="text-lg font-semibold text-foreground">{selectedDistrict.name}</h4>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-surface rounded-lg p-4">
                      <div className="text-2xl font-bold text-foreground">{selectedDistrict.projects}</div>
                      <div className="text-sm text-muted-foreground">Active Projects</div>
                    </div>
                    <div className="bg-surface rounded-lg p-4">
                      <div className="text-2xl font-bold text-foreground">{selectedDistrict.beneficiaries.toLocaleString()}</div>
                      <div className="text-sm text-muted-foreground">Beneficiaries</div>
                    </div>
                  </div>
                  
                  <p className="text-muted-foreground">{selectedDistrict.description}</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Icon name="MapPin" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Click on a district marker to view detailed information</p>
                </div>
              )}
            </div>

            {/* District List */}
            <div className="bg-card rounded-xl p-6 card-shadow">
              <h3 className="text-xl font-semibold text-foreground mb-4">All Districts</h3>
              <div className="space-y-3">
                {districts.map((district) => (
                  <div
                    key={district.id}
                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-smooth ${
                      selectedDistrict?.id === district.id
                        ? 'bg-primary/10 border border-primary/20' :'bg-surface hover:bg-muted'
                    }`}
                    onClick={() => setSelectedDistrict(district)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 ${district.color} rounded-full`} />
                      <span className="font-medium text-foreground">{district.name}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {district.projects} projects • {district.beneficiaries} people
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GeographicImpactMap;